import React, { useEffect, useState, useRef } from 'react';
import api from '../../config/axios';
import { orderSubtotal, orderTax, orderTotal, taxRateLabel } from '../../utils/tax';
import { useLocation, useNavigate } from 'react-router-dom';
import './OrderPayment.css';

const OrderGuestPayment: React.FC = () => {
  const [order, setOrder] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [momoLoading, setMomoLoading] = useState(false);
  const [isPolling, setIsPolling] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const pollingRef = useRef<NodeJS.Timeout | null>(null);

  // Lấy orderId từ query
  const params = new URLSearchParams(location.search);
  const orderId = params.get('orderId');

  // Lấy đơn hàng
  useEffect(() => {
    const fetchOrder = async () => {
      if (!orderId) return;
      setLoading(true);
      try {
        const res = await api.get(`/api/orders/${orderId}`);
        const orderData = res.data.data || res.data;
        setOrder(orderData);
        
        // Nếu đơn hàng đang processing, bắt đầu polling
        if (orderData.status === 'processing') {
          startPolling();
        }
      } catch {
        setError('Không tìm thấy đơn hàng');
      } finally {
        setLoading(false);
      }
    };
    fetchOrder();
  }, [orderId]);

  // Cleanup polling khi component unmount
  useEffect(() => {
    return () => {
      if (pollingRef.current) {
        clearTimeout(pollingRef.current);
      }
    };
  }, []);

  // Hàm bắt đầu polling
  const startPolling = () => {
    if (isPolling) return;
    setIsPolling(true);
    checkPaymentStatus();
  };

  // Hàm dừng polling
  const stopPolling = () => {
    setIsPolling(false);
    if (pollingRef.current) {
      clearTimeout(pollingRef.current);
      pollingRef.current = null;
    }
  };

  // Kiểm tra trạng thái thanh toán
  const checkPaymentStatus = async () => {
    if (!orderId || !isPolling) return;
    
    try {
      console.log('Checking payment status for order:', orderId);
      const statusRes = await api.get(`/api/payment/momo/status-guest/${orderId}`);
      
      console.log('Payment status response:', statusRes.data);
      
      if (statusRes.data.data.status === 'paid') {
        console.log('Payment successful! Updating UI...');
        setSuccess('Thanh toán Momo thành công!');
        setOrder((prevOrder: any) => ({
          ...prevOrder,
          status: 'paid',
          amountPaid: statusRes.data.data.amountPaid,
          paidAt: statusRes.data.data.paidAt
        }));
        stopPolling();
        setTimeout(() => navigate('/'), 3000);
        return;
      } else if (statusRes.data.data.status === 'processing') {
        console.log('Payment still processing, checking again in 3 seconds...');
        pollingRef.current = setTimeout(checkPaymentStatus, 3000);
      } else if (statusRes.data.data.status === 'pending') {
        console.log('Payment failed or cancelled, stopping polling');
        stopPolling();
      } else {
        console.log('Payment status:', statusRes.data.data.status);
        pollingRef.current = setTimeout(checkPaymentStatus, 3000);
      }
    } catch (err) {
      console.error('Error checking payment status:', err);
      // Thử lại sau 5 giây nếu có lỗi
      pollingRef.current = setTimeout(checkPaymentStatus, 5000);
    }
  };

  // Thuế đã chốt theo từng món khi đặt đơn; cộng lại từ chính các món.
  const subtotal = order ? orderSubtotal(order) : 0;
  const tax = order ? Math.round(orderTax(order)) : 0;
  const total = order ? Math.round(orderTotal(order)) : 0;

  // Thanh toán qua Momo
  const handlePay = async () => {
    setError(''); 
    setSuccess('');
    if (!orderId) return;
    
    try {
      setMomoLoading(true);
      const res = await api.post(
        '/api/payment/momo/create-guest',
        {
          orderId: orderId,
          amount: total,
          orderInfo: `Thanh toan don hang ${orderId.slice(-6).toUpperCase()}`
        }
      );
      
      if (res.data.success) {
        if (res.data.data.payUrl) {
          // Cập nhật trạng thái đơn hàng thành processing trước khi chuyển hướng
          setOrder((prevOrder: any) => ({
            ...prevOrder,
            status: 'processing'
          }));
          
          // Chuyển hướng trong cùng trang thay vì mở tab mới
          window.location.href = res.data.data.payUrl;
        } else {
          setSuccess(res.data.data.message || 'Đơn hàng đã được xử lý thanh toán.');
        }
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Lỗi khi tạo thanh toán Momo');
    } finally {
      setMomoLoading(false);
    }
  };

  return (
    <div className="order-payment-bg">
      <div className="order-payment-container">
        <div className="order-payment-header">
          <button
            onClick={() => navigate(-1)}
            className="order-payment-back-btn"
            title="Quay lại"
          >
            ←
          </button>
          <h2 className="order-payment-title">THANH TOÁN ĐƠN HÀNG</h2>
        </div>
        {error && <div className="order-payment-message order-payment-error">{error}</div>}
        {success && <div className="order-payment-message order-payment-success">{success}</div>}
        {loading && <div className="order-payment-loading">Đang xử lý...</div>}
        {order && (
          <div className="order-payment-form" style={{ maxWidth: 520, margin: '0 auto' }}>
            <div className="order-payment-info">
              <div className="order-payment-info-item">
                <span className="order-payment-info-label">Mã đơn:</span>
                <span className="order-payment-info-value order-payment-id">{order._id.slice(-6).toUpperCase()}</span>
              </div>
              <div className="order-payment-info-item">
                <span className="order-payment-info-label">Bàn:</span>
                                        <span className="order-payment-info-value">
                          {order.mergedTables && order.mergedTables.length > 0 
                            ? order.mergedTables.map((table: any) => table.name).join(', ')
                            : order.tableId?.name || order.tableId
                          }
                        </span>
              </div>
              <div className="order-payment-info-item">
                <span className="order-payment-info-label">Khách:</span>
                <span className="order-payment-info-value">{order.customerName || '---'}</span>
              </div>
              <div className="order-payment-info-item">
                <span className="order-payment-info-label">Thời gian:</span>
                <span className="order-payment-info-value">{new Date(order.createdAt).toLocaleString()}</span>
              </div>
              <div className="order-payment-info-item menu-list" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: 4 }}>
                <span className="order-payment-info-label" style={{ marginBottom: 2 }}>Menu:</span>
                <ul style={{ margin: 0, paddingLeft: 18, fontSize: 14 }}>
                  {order.items.map((item: any, idx: number) => (
                    <li key={idx}>
                      {item.name} x {item.quantity} <span style={{ color: '#888' }}>- {(item.price * item.quantity).toLocaleString()} đ</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="order-payment-info-item">
                <span className="order-payment-info-label">Tạm tính:</span>
                <span className="order-payment-info-value">{subtotal.toLocaleString()} đ</span>
              </div>
              <div className="order-payment-info-item">
                <span className="order-payment-info-label">Thuế ({order ? taxRateLabel(order) : '0%'}):</span>
                <span className="order-payment-info-value">{tax.toLocaleString()} đ</span>
              </div>
              <div className="order-payment-info-item" style={{ fontWeight: 700, fontSize: 18, borderTop: '1px solid #e2e8f0', paddingTop: 6, marginTop: 4 }}>
                <span className="order-payment-info-label">Tổng cộng:</span>
                <span className="order-payment-info-value order-payment-total">{total.toLocaleString()} đ</span>
              </div>
              <div className="order-payment-info-item">
                <span className="order-payment-info-label">Trạng thái:</span>
                <span className="order-payment-info-value" style={{ 
                  color: order.status === 'paid' ? '#28a745' : 
                         order.status === 'processing' ? '#ffc107' : '#6c757d',
                  fontWeight: 'bold'
                }}>
                  {order.status === 'paid' ? 'Đã thanh toán' : 
                   order.status === 'processing' ? 'Đang xử lý' : 
                   order.status === 'pending' ? 'Chờ thanh toán' : order.status}
                </span>
              </div>
            </div>
            
            <button 
              className="order-payment-pay-btn" 
              onClick={handlePay} 
              disabled={momoLoading || order.status === 'paid' || isPolling}
            >
              {order.status === 'paid' ? 'Đã thanh toán' : 
               order.status === 'processing' ? (isPolling ? 'Đang kiểm tra thanh toán...' : 'Đang xử lý thanh toán...') :
               momoLoading ? 'Đang xử lý...' : 'Thanh toán qua Momo'}
            </button>
            
            {isPolling && (
              <div style={{ 
                textAlign: 'center', 
                marginTop: 16, 
                padding: 12, 
                background: '#f0f9ff', 
                border: '1px solid #0ea5e9', 
                borderRadius: 8,
                fontSize: 14,
                color: '#0369a1'
              }}>
                🔄 Đang kiểm tra trạng thái thanh toán... Vui lòng chờ
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default OrderGuestPayment; 